export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu']
export const NUM_DATA_ROWS = 11

/**
 * SPLH Productivity Levels - Used as denominators to calculate variable labor hours
 * Formula: variableHours = actualSales / splhTarget
 *
 * @description
 * These values represent Sales Per Labor Hour targets used to calculate
 * variable theoretical labor hours based on actual sales performance.
 *
 * @history
 * Original values (pre-2026-01-08):
 *   FOH: weekday=316, weekend=230
 *   BOH: weekday=345, weekend=316
 *
 * Updated 2026-01-08:
 *   - Standard stores: +4.5% adjustment (multiplier: 1.045)
 *   - Las Vegas stores: +2.25% adjustment (multiplier: 1.0225)
 *
 * Calculation examples:
 *   Standard FOH weekday: 316 * 1.045 = 330.22
 *   Vegas FOH weekday:    316 * 1.0225 = 323.11
 */

/**
 * Standard store SPLH targets (+4.5% adjustment from original values)
 *
 * Calculated as: originalValue * 1.045
 *   FOH weekday: 316 * 1.045 = 330.22
 *   FOH weekend: 230 * 1.045 = 240.35
 *   BOH weekday: 345 * 1.045 = 360.53
 *   BOH weekend: 316 * 1.045 = 330.22
 */
const STANDARD_THEO_HOURS = {
  foh: { weekday: 330.22, weekend: 240.35 },
  boh: { weekday: 360.53, weekend: 330.22 }
}

/**
 * Las Vegas store SPLH targets (+2.25% adjustment from original values)
 * Las Vegas stores have a smaller adjustment due to higher operating costs.
 *
 * Calculated as: originalValue * 1.0225
 *   FOH weekday: 316 * 1.0225 = 323.11
 *   FOH weekend: 230 * 1.0225 = 235.18
 *   BOH weekday: 345 * 1.0225 = 352.76
 *   BOH weekend: 316 * 1.0225 = 323.11
 */
const LAS_VEGAS_THEO_HOURS = {
  foh: { weekday: 323.11, weekend: 235.18 },
  boh: { weekday: 352.76, weekend: 323.11 }
}

/**
 * Las Vegas store identifiers
 * These stores receive the reduced adjustment rate (+2.25% vs +4.5%)
 */
const LAS_VEGAS_STORES = ['Boca', 'Green Valley', 'Palazzo', 'South Rainbow']

/**
 * Get theoretical hours configuration for a specific store
 * Returns hardcoded values based on store location (Las Vegas vs standard)
 *
 * @param {string} storeName - The store's short name (e.g., 'Boca', 'Legacy')
 * @returns {Object} Configuration object with foh and boh weekday/weekend values
 */
export function getTheoHoursConfig(storeName) {
  const isLasVegas = LAS_VEGAS_STORES.includes(storeName)
  return isLasVegas ? LAS_VEGAS_THEO_HOURS : STANDARD_THEO_HOURS
}

/**
 * @deprecated Use getTheoHoursConfig(storeName) instead for store-specific values
 * Legacy static configuration - kept for backward compatibility
 * Note: These are the ORIGINAL values, not the adjusted values
 */
export const THEO_HOURS_CONFIG = {
  foh: {
    weekday: 316,
    weekend: 230
  },
  boh: {
    weekday: 345,
    weekend: 316
  }
}